import { useRef, useState } from 'react';

import {
  TertiaryButton,
  Typography,
} from '@changegps/horizon-component-library';
import { Box, Paper } from '@mui/material';
import { toast } from 'react-toastify';

import { useUserQuery } from '@/features/users';

import { useDeleteImage, useUserProfileFormContext } from '../hooks';

import { DeleteUserImageDialog } from './DeleteUserImageDialog';

export const UserImages = () => {
  const form = useUserProfileFormContext();
  const { data: user } = useUserQuery();
  const { mutate: deleteImage } = useDeleteImage();

  // Refs for file inputs
  const profileInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  // State for preview URLs
  const [profilePreview, setProfilePreview] = useState<string | null>('');
  const [signaturePreview, setSignaturePreview] = useState<string | null>('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fileType, setFileType] = useState<'profile' | 'signature' | null>(
    null,
  );

  const [isDeleting, setIsDeleting] = useState(false);

  // Handle file selection
  const handleUpload =
    (type: 'profile' | 'signature') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        // Validate file extension
        const allowedExtensions =
          type === 'profile' ? ['jpg', 'jpeg', 'png'] : ['png'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
          const allowedFormats = type === 'profile' ? 'JPG and PNG' : 'PNG';
          toast.error(`Only ${allowedFormats} files are allowed`);
          event.target.value = '';
          return;
        }

        // Generate temporary URL for preview
        const previewUrl = URL.createObjectURL(file);

        if (type === 'profile') {
          setProfilePreview(previewUrl);
          form.setValue('profile_pic', file); // Bind to form state
        } else {
          setSignaturePreview(previewUrl);
          form.setValue('signature_pic', file); // Bind to form state
        }

        // Clean up object URL to avoid memory leaks
        return () => URL.revokeObjectURL(previewUrl);
      }
    };

  // Open delete confirmation dialog or remove preview directly
  const handleDelete = (type: 'profile' | 'signature') => () => {
    if (type === 'profile') {
      if (profilePreview) {
        form.unregister('profile_pic'); // Remove from form state
        setProfilePreview('');
        if (profileInputRef.current) {
          profileInputRef.current.value = ''; // Reset input field
        }
      } else {
        setFileType(type);
        setDialogOpen(true);
      }
    } else if (type === 'signature') {
      if (signaturePreview) {
        form.unregister('signature_pic'); // Remove from form state
        setSignaturePreview('');
        if (signatureInputRef.current) {
          signatureInputRef.current.value = ''; // Reset input field
        }
      } else {
        setFileType(type);
        setDialogOpen(true);
      }
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = () => {
    if (!fileType) return;

    setIsDeleting(true); // Set loading state

    deleteImage(
      { file: `${fileType}_pic` },
      {
        onSuccess: () => {
          setDialogOpen(false);
          setIsDeleting(false); // Reset loading state
          toast.success(`Successfully deleted ${fileType}`);
        },
        onSettled: () => {
          if (fileType === 'profile') {
            form.unregister('profile_pic'); // Remove profile_pic from form state
            setProfilePreview('');
            if (profileInputRef.current) {
              profileInputRef.current.value = '';
            }
          } else {
            form.unregister('signature_pic'); // Remove signature_pic from form state
            setSignaturePreview('');
            if (signatureInputRef.current) {
              signatureInputRef.current.value = '';
            }
          }
        },
        onError: () => {
          toast.error(`Error deleting ${fileType}`);
        },
      },
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: '48px',
      }}
    >
      {/* Profile Picture Section */}
      <Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            marginY: '0.5em',
          }}
        >
          Profile Picture
        </Typography>
        {profilePreview || user?.profile_pic ? (
          <img
            src={profilePreview || user?.profile_pic || ''}
            alt="Profile"
            style={{
              border: '2px solid #EFEFEF',
              width: '200px',
              height: '200px',
              borderRadius: '10px',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Typography
            sx={{
              width: '200px',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #ccc',
              borderRadius: '10px',
              color: '#888',
            }}
          >
            No Profile Uploaded
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginY: '1em',
            gap: '4px',
          }}
        >
          {/* Hidden file input */}
          <input
            type="file"
            accept="image/jpeg,image/png,image/jpg" // Allow JPG, JPEG, PNG
            style={{ display: 'none' }}
            ref={profileInputRef}
            onChange={handleUpload('profile')}
          />
          {/* Trigger file input */}
          <TertiaryButton
            fullWidth
            onClick={() => profileInputRef.current?.click()}
          >
            Upload
          </TertiaryButton>
          <TertiaryButton
            fullWidth
            onClick={handleDelete('profile')}
            disabled={!profilePreview && !user?.profile_pic} // Enable when there's a preview or uploaded pic
          >
            Delete
          </TertiaryButton>
        </Box>
      </Box>

      {/* Signature Section */}
      <Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            marginY: '0.5em',
          }}
        >
          Signature
        </Typography>
        {signaturePreview || user?.signature_pic ? (
          <img
            src={signaturePreview || user?.signature_pic || ''}
            alt="Signature"
            style={{
              width: '200px',
              height: '200px',
              borderRadius: '10px',
              objectFit: 'contain',
            }}
          />
        ) : (
          <Typography
            sx={{
              width: '200px',
              height: '200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed #ccc',
              borderRadius: '10px',
              color: '#888',
            }}
          >
            No Signature Uploaded
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginY: '1em',
            gap: '4px',
          }}
        >
          {/* Hidden file input */}
          <input
            type="file"
            accept="image/png"
            style={{ display: 'none' }}
            ref={signatureInputRef}
            onChange={handleUpload('signature')}
          />
          {/* Trigger file input */}
          <TertiaryButton
            fullWidth
            onClick={() => signatureInputRef.current?.click()}
          >
            Upload
          </TertiaryButton>
          <TertiaryButton
            fullWidth
            onClick={handleDelete('signature')}
            disabled={!signaturePreview && !user?.signature_pic}
          >
            Delete
          </TertiaryButton>
        </Box>
      </Box>

      {/* Letter Template Signature Preview */}
      <Box sx={{ width: '350px' }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            marginY: '0.5em',
          }}
        >
          Letter Template Signature Preview
        </Typography>
        <Paper
          sx={{
            padding: 3,
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            textAlign: 'left', // Align text to the left
          }}
          elevation={1}
        >
          {signaturePreview || user?.signature_pic ? (
            <img
              src={signaturePreview || user?.signature_pic || ''}
              alt="Signature"
              style={{
                width: '200px',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <Typography
              sx={{
                width: '200px',
                height: '127px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                color: '#888',
              }}
            >
              No Signature Uploaded
            </Typography>
          )}
          <Typography>{user?.full_name}</Typography>
          <Typography>{user?.company_name}</Typography>
          <Typography>{`${user?.position} | ${user?.quals}`}</Typography>
        </Paper>
      </Box>

      {fileType && (
        <DeleteUserImageDialog
          open={dialogOpen}
          isPending={isDeleting}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          imageType={fileType}
        />
      )}
    </Box>
  );
};
